import React, { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Plus, Loader2, Trash2, ChevronDown, ChevronRight, Play } from 'lucide-react';
import { tl } from '../../lib/testingLabApi';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

export const SuitesTab = () => {
  const [suites, setSuites] = useState([]);
  const [cases, setCases] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showCreateCase, setShowCreateCase] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadSuites = async () => {
    try {
      const data = await tl.listSuites();
      setSuites(data.items || []);
    } catch (e) { toast.error(e.message); }
  };

  const loadCases = async (suiteId) => {
    try {
      const data = await tl.listCases(suiteId);
      setCases(data.items || []);
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => { loadSuites(); }, []);
  useEffect(() => { if (selected) loadCases(selected.test_suite_id); else setCases([]); }, [selected]);

  const create = async (body) => {
    setBusy(true);
    try {
      await tl.createSuite(body);
      setShowCreate(false);
      await loadSuites();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const createCase = async (body) => {
    if (!selected) return;
    setBusy(true);
    try {
      await tl.createCase({ ...body, test_suite_id: selected.test_suite_id });
      setShowCreateCase(false);
      await loadCases(selected.test_suite_id);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-12 gap-4 h-[calc(100vh-220px)]" data-testid="suites-tab">
      <div className="col-span-4">
        <Card className="h-full flex flex-col">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold">Test Suites</h3>
            <Button size="sm" onClick={() => setShowCreate(true)} data-testid="suite-new"><Plus className="w-3 h-3" /></Button>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1.5">
              {suites.map((s) => (
                <button key={s.test_suite_id} onClick={() => setSelected(s)}
                  className={cn(
                    'w-full text-left p-2.5 rounded border text-xs',
                    selected?.test_suite_id === s.test_suite_id ? 'border-violet-500 bg-violet-500/5' : 'border-border hover:border-violet-500/50'
                  )}
                  data-testid={`suite-row-${s.test_suite_id}`}>
                  <div className="flex items-center gap-1.5">
                    {s.module && <Badge variant="outline" className="text-[9px] h-4">{s.module}</Badge>}
                    <Badge className="text-[9px] h-4" variant={s.status === 'active' ? 'default' : 'outline'}>{s.status}</Badge>
                  </div>
                  <div className="font-medium mt-0.5">{s.title}</div>
                  {s.purpose && <div className="text-[10px] text-muted-foreground line-clamp-2">{s.purpose}</div>}
                </button>
              ))}
              {suites.length === 0 && (
                <div className="text-center py-12 text-xs text-muted-foreground">
                  No suites yet
                </div>
              )}
            </div>
          </ScrollArea>
        </Card>
      </div>

      <div className="col-span-8">
        {selected ? (
          <Card className="h-full flex flex-col" data-testid="suite-detail">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold">{selected.title}</h3>
                <p className="text-[11px] text-muted-foreground">{cases.length} test case{cases.length !== 1 ? 's' : ''}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowCreateCase(true)} data-testid="case-new"><Plus className="w-3 h-3 mr-1" /> Case</Button>
                <Button size="sm" variant="ghost" onClick={async () => {
                  if (window.confirm('Delete suite + all its cases?')) {
                    await tl.deleteSuite(selected.test_suite_id);
                    setSelected(null);
                    await loadSuites();
                  }
                }} data-testid="suite-delete">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-3 space-y-2">
                {cases.map((c) => (
                  <CaseRow key={c.test_case_id} testCase={c} suite={selected}
                    onChanged={() => loadCases(selected.test_suite_id)} />
                ))}
                {cases.length === 0 && (
                  <div className="text-center py-12 text-xs text-muted-foreground">No test cases. Add one to get started.</div>
                )}
              </div>
            </ScrollArea>
          </Card>
        ) : (
          <Card className="h-full flex items-center justify-center text-muted-foreground text-sm">
            Select a suite
          </Card>
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="suite-create-dialog">
          <DialogHeader><DialogTitle>New test suite</DialogTitle></DialogHeader>
          <CreateSuiteForm onSubmit={create} disabled={busy} />
        </DialogContent>
      </Dialog>

      <Dialog open={showCreateCase} onOpenChange={setShowCreateCase}>
        <DialogContent data-testid="case-create-dialog">
          <DialogHeader><DialogTitle>New test case</DialogTitle></DialogHeader>
          <CreateCaseForm onSubmit={createCase} disabled={busy} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CaseRow = ({ testCase, suite, onChanged }) => {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  const startCaseInNewSession = async () => {
    setBusy(true);
    try {
      // Create a session bound to the suite
      const sess = await tl.createSession({
        title: `Run: ${testCase.title}`,
        test_suite_id: suite.test_suite_id,
        test_case_id: testCase.test_case_id,
      });
      // Materialize step runs for this case
      await tl.startCaseInSession(sess.test_session_id, testCase.test_case_id);
      toast.success('Session started — opening Runner');
      window.open(`/testing-lab/run/${encodeURIComponent(sess.test_session_id)}`, '_blank', 'noopener,noreferrer');
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border rounded text-xs overflow-hidden hover:border-violet-500/50 transition-colors"
      data-testid={`case-row-${testCase.test_case_id}`}>
      <div className="p-2.5 space-y-1.5">
        <div className="flex items-start gap-2">
          <button onClick={() => setExpanded(!expanded)}
            className="mt-0.5 flex-shrink-0 text-muted-foreground hover:text-foreground"
            data-testid={`case-expand-${testCase.test_case_id}`}>
            {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge className="text-[9px] h-4" variant="outline">{testCase.test_type}</Badge>
              <Badge className="text-[9px] h-4" variant={testCase.priority === 'P0' ? 'destructive' : 'outline'}>{testCase.priority}</Badge>
              <Badge className={cn(
                'text-[9px] h-4',
                testCase.status === 'passed' && 'bg-emerald-500 text-white',
                testCase.status === 'failed' && 'bg-red-500 text-white',
                testCase.status === 'in_test' && 'bg-blue-500 text-white',
              )} variant={['passed', 'failed', 'in_test'].includes(testCase.status) ? 'default' : 'outline'}>
                {testCase.status}
              </Badge>
              {testCase.steps?.length > 0 && (
                <span className="text-[10px] text-muted-foreground">{testCase.steps.length} step{testCase.steps.length !== 1 ? 's' : ''}</span>
              )}
            </div>
            <div className="font-medium mt-1">{testCase.title}</div>
            {testCase.expected_outcome && <div className="text-[10px] text-muted-foreground mt-0.5">Expected: {testCase.expected_outcome}</div>}
          </div>
          <Button size="sm" onClick={startCaseInNewSession} disabled={busy}
            className="h-7 text-[10px] flex-shrink-0" data-testid={`case-run-${testCase.test_case_id}`}>
            {busy ? <Loader2 className="w-3 h-3" /> : <><Play className="w-3 h-3 mr-1" /> Run</>}
          </Button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-border/50 bg-muted/30 px-2.5 py-2 space-y-2">
          {(testCase.must_work?.length || 0) + (testCase.should_work?.length || 0) > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {testCase.must_work?.length > 0 && (
                <div>
                  <div className="text-[9px] uppercase font-semibold text-red-600 dark:text-red-400 mb-0.5">Must work</div>
                  <ul className="space-y-0.5">
                    {testCase.must_work.map((m, i) => <li key={i} className="text-[10px]">• {m}</li>)}
                  </ul>
                </div>
              )}
              {testCase.should_work?.length > 0 && (
                <div>
                  <div className="text-[9px] uppercase font-semibold text-amber-600 dark:text-amber-400 mb-0.5">Should work</div>
                  <ul className="space-y-0.5">
                    {testCase.should_work.map((m, i) => <li key={i} className="text-[10px]">• {m}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          <div>
            <div className="text-[9px] uppercase font-semibold text-muted-foreground mb-1">Steps</div>
            {testCase.steps?.length > 0 ? (
              <ol className="space-y-1.5">
                {testCase.steps.map((s, i) => (
                  <li key={s.step_id || i} className="flex gap-2 text-[11px]">
                    <span className="w-4 h-4 rounded-full border border-border flex items-center justify-center text-[9px] flex-shrink-0">{s.order || i + 1}</span>
                    <div>
                      <div>{s.instruction}</div>
                      {s.expected_result && <div className="text-[10px] text-muted-foreground italic">→ {s.expected_result}</div>}
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-[10px] text-muted-foreground italic">No steps defined yet.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};



const CreateSuiteForm = ({ onSubmit, disabled }) => {
  const [title, setTitle] = useState('');
  const [moduleVal, setModuleVal] = useState('');
  const [purpose, setPurpose] = useState('');
  return (
    <div className="space-y-3">
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" data-testid="suite-title-input" />
      <Input value={moduleVal} onChange={(e) => setModuleVal(e.target.value)} placeholder="Module (e.g. ai, finance)" data-testid="suite-module-input" />
      <Input value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="Purpose" data-testid="suite-purpose-input" />
      <DialogFooter>
        <Button onClick={() => onSubmit({ title, module: moduleVal, purpose })} disabled={!title || disabled} data-testid="suite-create-submit">
          {disabled ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
        </Button>
      </DialogFooter>
    </div>
  );
};

const CreateCaseForm = ({ onSubmit, disabled }) => {
  const [title, setTitle] = useState('');
  const [outcome, setOutcome] = useState('');
  return (
    <div className="space-y-3">
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Case title" data-testid="case-title-input" />
      <Input value={outcome} onChange={(e) => setOutcome(e.target.value)} placeholder="Expected outcome" data-testid="case-outcome-input" />
      <DialogFooter>
        <Button onClick={() => onSubmit({ title, expected_outcome: outcome })} disabled={!title || disabled} data-testid="case-create-submit">
          {disabled ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
        </Button>
      </DialogFooter>
    </div>
  );
};

export default SuitesTab;
